// landingTheme.js
// ===============================
// Marketing / landing palette — the dark, gradient-heavy visual language for
// the PUBLIC-facing surfaces only (HomePage sections + the auth brand panel).
//
// This is intentionally SEPARATE from the signed-in app design system in
// tokens.js (light slate + single teal accent). Do not consume this from
// signed-in app components — use tokens.js there. appTheme.js references this
// only for the landing-specific hero typography variants.
// ===============================

// Color configuration
export const COLORS = {
  // Background colors
  BACKGROUND: {
    PRIMARY: "#A1CD73",
    SECONDARY: "#3F9A82",
    TERTIARY: "#427676",
    DEFAULT: "#ffffffff",

    CODEBLOCK: "rgba(0,0,0,0.1)",

    CARD: "#1A1A1A",
    CARD_HOVER: "rgba(0,0,0,0.03)",
  },
  // Gradient configuration
  GRADIENTS: {
    // configurable angle + optional custom background
    MAIN: (deg = 90, bg) => {
      const b = bg || COLORS.BACKGROUND;
      return `linear-gradient(${deg}deg, ${b.PRIMARY} 0%, ${b.SECONDARY} 33%, ${b.TERTIARY} 66%, #3B596A 100%)`;
    },

    LIGHT: "linear-gradient(135deg, #F9F6E7 0%, #FCFFF5 60%, #E1E6FA 100%)",
    BACKGROUND: "linear-gradient(135deg, #3F9A82 0%, #427676 100%)",
    TEXT: "linear-gradient(90deg, #FFFFFF, #00D4FF)",
    BUTTON: "linear-gradient(90deg, #00D4FF, #8B5CF6)",
    // BUTTON_HOVER: 'linear-gradient(90deg, #00B8E6, #7C3AED)',
  },

  // Text colors
  TEXT: {
    PRIMARY: "#000000ff",
    SECONDARY: "#575757ff",
    HINT: "rgba(0, 0, 0, 0.6)",
    DISABLED: "rgba(0, 0, 0, 0.38)",
  },

  // Border colors
  BORDER: {
    DEFAULT: "rgba(0, 0, 0, 0.1)",
    HOVER: "#A1CD73",
  },

  // Shadows
  SHADOW: {
    CARD: "0 4px 20px rgba(0, 0, 0, 0.3)",
    CARD_HOVER: "0 8px 30px rgba(0, 212, 255, 0.1)",
    TEXT: "0 2px 4px rgba(0,0,0,0.3)",
  },

  // Icon colors
  ICON: {
    PRIMARY: "#A1CD73",
    SECONDARY: "rgba(63,154,130,.12)",
  },

  // Status colors
  STATUS: {
    SUCCESS: "#A1CD73",
    WARNING: "#FFA726",
    ERROR: "#F44336",
    DEFAULT: "#9E9E9E",
    INFO: "#64B5F6",
  },
};

// ===============================
// Typography configuration
// ===============================
export const TYPOGRAPHY = {
  FONT_FAMILY: ["Helvetica", "Inter", "Arial", "sans-serif"].join(","),

  SIZES: {
    H1: { xs: "3rem", sm: "4rem", md: "5rem" },
    H2: { xs: "2rem", sm: "2.5rem", md: "3rem" },
    H3: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
    H4: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
    BODY: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
  },

  WEIGHTS: {
    REGULAR: 400,
    MEDIUM: 500,
    SEMI_BOLD: 600,
    BOLD: 700,
  },
};

// ===============================
// Animation configuration
// ===============================
export const ANIMATIONS = {
  TRANSITION: "all 0.3s ease",
  HOVER_TRANSFORM: "translateY(-4px)",
};

// ===============================
// Spacing configuration
// ===============================
export const SPACING = {
  CARD_PADDING: 4,
  SECTION_MARGIN: 6,
  GRID_GAP: 4,
};

// ===============================
// Border configuration
// ===============================
export const BORDERS = {
  RADIUS: {
    CARD: 12,
    BUTTON: 8,
  },
};

// ===============================
// Blur / backdrop effects configuration
// ===============================
export const EFFECTS = {
  BLUR: "blur(10px)",
  BACKDROP: "rgba(26, 26, 26, 0.9)",
};

// ===============================
// Home page sections configuration
// ===============================
export const HOMEPAGE_SECTIONS = {
  HERO: {
    BACKGROUND: {
      GRADIENT: "linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)",
      OVERLAY:
        "radial-gradient(circle at 50% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%)",
    },
    TITLE: {
      GRADIENT: "linear-gradient(90deg, #FFFFFF, #00D4FF)",
      SHADOW: "0 2px 4px rgba(0,0,0,0.3)",
    },
    FEATURE_CARD: {
      BACKGROUND: "#1A1A1A",
      SHADOW: "0 4px 20px rgba(0, 0, 0, 0.3)",
    },
  },

  EXPERIMENTS: {
    BACKGROUND: {
      GRADIENT: "linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)",
      OVERLAY:
        "radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)",
    },
    TITLE: {
      GRADIENT: "linear-gradient(90deg, #FFFFFF, #8B5CF6)",
      SHADOW: "0 2px 4px rgba(0,0,0,0.3)",
    },
    CHART_CARD: {
      BACKGROUND: "#1A1A1A",
      SHADOW: "0 4px 20px rgba(0, 0, 0, 0.3)",
    },
  },

  CLUSTER_STATUS: {
    BACKGROUND: {
      GRADIENT: "linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)",
      OVERLAY:
        "radial-gradient(circle at 50% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%)",
    },
    TITLE: {
      GRADIENT: "linear-gradient(90deg, #FFFFFF, #00D4FF)",
      SHADOW: "0 2px 4px rgba(0,0,0,0.3)",
    },
    STAT_CARD: {
      BACKGROUND: "#1A1A1A",
      SHADOW: "0 4px 20px rgba(0, 0, 0, 0.3)",
    },
  },
};

// ===============================
// Layout configuration
// ===============================
export const LAYOUT = {
  CONTAINER: {
    MAX_WIDTH: "100%",
    PADDING: { X: 4, Y: 3 },
    MARGIN: { X: 0 },
  },
};

// ===============================
// Button configuration
// ===============================
export const BUTTONS = {
  GET_STARTED: {
    BACKGROUND: "#00D4FF",
    COLOR: "#0A0A0A",
    PADDING: "16px 48px",
    FONT_SIZE: "1.8rem",
    FONT_WEIGHT: 600,
    BORDER_RADIUS: "8px",
    SHADOW: "0 4px 12px rgba(0, 212, 255, 0.3)",
    MARGIN_TOP: 6,
  },
};

// ===============================
// Status color configuration
// ===============================
export const statusColorMap = {
  error: {
    backgroundColor: COLORS.STATUS.ERROR,
    color: "white",
    hoverBackgroundColor: "#d32f2f",
  },
  warning: {
    backgroundColor: COLORS.STATUS.WARNING,
    color: "white",
    hoverBackgroundColor: "#f57c00",
  },
  success: {
    backgroundColor: COLORS.STATUS.SUCCESS,
    color: "white",
    hoverBackgroundColor: "#388e3c",
  },
  default: {
    backgroundColor: COLORS.STATUS.DEFAULT,
    color: "white",
    hoverBackgroundColor: "#757575",
  },
  info: {
    backgroundColor: COLORS.STATUS.INFO,
    color: "white",
    hoverBackgroundColor: "#1976d2",
  },
};

// Utility function: get color styles based on status
export const getStatusStyle = (status) => {
  const colorConfig = statusColorMap[status] || statusColorMap.default;
  return {
    backgroundColor: colorConfig.backgroundColor,
    color: colorConfig.color,
    "&:hover": { backgroundColor: colorConfig.hoverBackgroundColor },
  };
};
